'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ArcElement, Chart as ChartJS, Legend, Tooltip, TooltipItem } from 'chart.js'
import { AnimateNumber } from 'motion-plus/react'
import { Doughnut } from 'react-chartjs-2'

import DynamicIcon from '@/components/DynamicIcon'
import { Kicker } from '@/components/Kicker'
import { Button } from '@/components/ui/button'
import { useWinsContext } from '@/providers/WinsProvider'
import { badgeColorMap, fullColorMapHSL } from '@/utilities/badgeColorMap'
import { kebabToCamel } from '@/utilities/toCamel'
import { cn } from '@/utilities/ui'

import type { IconName } from '@/components/DynamicIcon'

ChartJS.register(ArcElement, Tooltip, Legend)

type TopicChartColors = {
  background: string
  hover: string
}

type ChartDisplayTopic = {
  name: string
  id: number
  count: number
  value: number
  color: string
  icon: string
}

const getTopicChartColors = (color: string): TopicChartColors => {
  if (color.includes('-dark')) {
    const baseColor = color.replace('-dark', '')
    return {
      background: `hsl(${fullColorMapHSL[baseColor][500]})`,
      hover: `hsl(${fullColorMapHSL[baseColor][400]})`,
    }
  }
  return {
    background: `hsl(${fullColorMapHSL[color][300]})`,
    hover: `hsl(${fullColorMapHSL[color][200]})`,
  }
}

/**
 * Doughnut chart of wins by topic for the wins data viz block.
 */
export const ChartTopic = () => {
  const { filteredWins, winTopics } = useWinsContext()
  const chartRef = useRef<ChartJS<'doughnut'> | null>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const [hiddenTopicIds, setHiddenTopicIds] = useState<Set<number>>(new Set())

  const wins = filteredWins

  const chartDisplayTopics: ChartDisplayTopic[] = useMemo<ChartDisplayTopic[]>(() => {
    return [
      ...winTopics
        .map((topic) => ({
          name: topic.title,
          id: topic.id,
          count: wins.filter((win) =>
            win.topics?.some((t) => typeof t === 'object' && t.id === topic.id),
          ).length,
          value:
            wins.filter((win) =>
              win.topics?.some((t) => typeof t === 'object' && t.id === topic.id),
            ).length / wins.length || 0,
          color: topic.color as string,
          icon: topic.icon as string,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      {
        name: 'No Topic',
        id: 0,
        count: wins.filter((win) => win.topics?.length === 0).length,
        value: wins.filter((win) => win.topics?.length === 0).length / wins.length || 0,
        color: 'gray',
        icon: 'x',
      },
    ]
  }, [winTopics, wins])

  const doughnutData = {
    labels: chartDisplayTopics.map((topic) => topic.name),
    datasets: [
      {
        label: 'Wins by Topic',
        data: chartDisplayTopics.map((topic) => topic.value * 100),
        backgroundColor: chartDisplayTopics.map(
          (topic) => getTopicChartColors(topic.color as string).background,
        ),
        hoverBackgroundColor: chartDisplayTopics.map(
          (topic) => getTopicChartColors(topic.color as string).hover,
        ),
        borderWidth: 0,
        hoverOffset: 10,
        spacing: 4,
      },
    ],
    options: {
      layout: {
        padding: {
          top: 4,
          right: 4,
          bottom: 4,
          left: 4,
        },
      },
      width: 302,
      height: 302,
      cutout: '70%',
      borderRadius: '50%',
      borderWidth: 0,
      borderColor: 'hsl(0 0% 100%)',
      hoverBorderColor: 'hsl(0 0% 100%)',
      hoverBorderWidth: 2,
      hoverBorderRadius: '50%',
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (context: TooltipItem<'doughnut'>) => {
              const topicWinCount =
                chartDisplayTopics.find((topic) => topic.name === context.label)?.count ?? 0
              const activeWinCount = chartDisplayTopics
                .filter((topic) => !hiddenTopicIds.has(topic.id))
                .reduce((acc, curr) => acc + curr.count, 0)

              return `${'\u00A0'}${hiddenTopicIds.size > 0 ? ((topicWinCount / activeWinCount) * 100).toFixed(0) : context.parsed?.toFixed(0)}% [${topicWinCount} Win${topicWinCount && topicWinCount > 1 ? 's' : ''}]`
            },
          },
          caretSize: 6,
          padding: 16,
          borderWidth: 1,
          borderRadius: 16,
          borderColor: 'hsl(0 0% 19% / 0.2)',
          backgroundColor: 'hsl(0 0% 100%)',
          bodyColor: 'hsl(0 0% 19%)',
          titleColor: 'hsl(0 0% 19%)',
          titleMarginBottom: 8,
          titleFont: {
            family: 'Albert Sans, sans-serif',
            size: 16,
            weight: 600,
          },
          bodyFont: {
            family: 'Albert Sans, sans-serif',
            size: 16,
            weight: 400,
          },
        },
      },
    },
  }

  const toggleDataVisibility = (index: number, topicId: ChartDisplayTopic['id']) => {
    const chart = chartRef.current
    if (chart) {
      chart.toggleDataVisibility(index)
      chart.update()

      setHiddenTopicIds((prev) => {
        const next = new Set(prev)
        if (next.has(topicId)) {
          next.delete(topicId)
        } else {
          next.add(topicId)
        }
        return next
      })
    }
  }

  const areAllTopicsZero =
    chartDisplayTopics.length === 0 || chartDisplayTopics.every((topic) => topic.value === 0)

  useEffect(() => {
    const el = chartContainerRef.current
    if (!el) return
  }, [areAllTopicsZero])

  const activeWinCount = useMemo(
    () =>
      wins.filter(
        (win) =>
          win.topics?.some((topic) => {
            if (typeof topic === 'object') return !hiddenTopicIds.has(topic.id)
            return !hiddenTopicIds.has(topic)
          }) ||
          (!hiddenTopicIds.has(0) && win.topics?.length === 0),
      ).length,
    [hiddenTopicIds, wins],
  )

  return (
    <div className="flex flex-col rounded-xl bg-card p-4 lg:flex-1 lg:px-6 lg:pt-8 lg:pb-10">
      <Kicker tone="default" className="mb-4 w-fit">
        Win Topics
      </Kicker>

      {areAllTopicsZero ? (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-muted-foreground">No wins found</p>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-8">
          <div className="relative z-0 size-[220px] font-display text-foreground xl:size-[302px]">
            <div className="pointer-events-none absolute inset-0 -z-10 flex size-full items-center justify-center font-display text-3xl text-foreground">
              <p className="inline-flex">
                <span>
                  <AnimateNumber>{activeWinCount}</AnimateNumber>
                </span>
                <span>&nbsp;Win{activeWinCount !== 1 ? 's' : ''}</span>
              </p>
            </div>
            <Doughnut
              ref={chartRef as React.RefObject<ChartJS<'doughnut'>>}
              data={doughnutData}
              options={doughnutData.options}
            />
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {chartDisplayTopics.map((topic: ChartDisplayTopic, index: number) => {
              const isHidden = hiddenTopicIds.has(topic.id)
              const isZero = topic.value === 0
              const topicColor = kebabToCamel(topic.color as string)
              const topicColorObject = badgeColorMap[topicColor]

              return (
                <Button
                  key={topic.id ?? topic.name}
                  ghost
                  icon={false}
                  onClick={() => toggleDataVisibility(index, topic.id)}
                  className={cn(
                    'w-fit justify-start gap-2 border-none bg-transparent px-3 py-1 text-sm font-medium text-foreground transition-all duration-200 hover:border-none hover:bg-transparent',
                    isHidden || isZero ? 'line-through opacity-50 grayscale' : 'opacity-100',
                  )}
                  disabled={Boolean(isZero) ?? undefined}
                >
                  <span
                    className={cn(
                      'flex size-8 items-center justify-center rounded-xl',
                      topicColorObject.bg,
                      topicColorObject.text,
                    )}
                  >
                    <DynamicIcon name={topic.icon as IconName} className="size-4" />
                  </span>
                  {topic.name}
                  {`${'\u00A0\u00A0'}${((topic.value ?? 0) * 100).toFixed(0)}%`}
                </Button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
